import { selMessage } from "@selector/shared";
import { createRef, PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import { LoadingSpinner, type PanelGroupRef, PanelOrientation, PanelPosition, ResizableTabPanels } from "widgets";

import {
  type ChartLayoutState,
  type ColumnAnnotation,
  type DataSeries,
  type InputTableQuerySpec,
  type SplitPaneLayoutState,
  type View,
} from "@/graphql.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.js";
import { DATA_VIEWER_MODE, UNSPECIFIED_ENTITY_ID, VIEW } from "@/settings/settings.js";

import { getDataViewerPageTips, type ITip } from "../../app-utils/help.js";
import { notify } from "../../modules/notifications/notify.js";
import {
  addDataSeries,
  addGroupDataSeries,
  chartLayoutStateChanged,
  colorSettingsChanged,
  filteredConditionsChanged,
  getDefaultDatasetId,
  type IColorSettings,
  type IColumnAnnotationFilter,
  type IDatasetViewerState,
  isTimeSeriesEnabled,
  loadAvailableDatasets,
  loadDatasetAndDataSeries,
  searchDataSeries,
  searchGroupDataSeries,
  selectedConditionsChanged,
  type SelectedDataSeries,
  selectedDataSeriesChanged,
  splitBySelectedCondition,
  splitPaneLayoutStateChanged,
  toggleFilterPanel,
} from "../../redux/modules/dataset-viewer.js";
import { loadView } from "../../redux/modules/saved-view.js";
import { type IAppState } from "../../redux/reducer.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import TipTour from "../core/TipTour.js";
import ChartSettings from "./ChartSettings.js";
import ChartToolbar from "./ChartToolbar.js";
import DatasetChart from "./DatasetChart.js";
import DatasetViewerTable from "./DatasetViewerTable.js";
import FilterPanel from "./FilterPanel.js";

export interface IDatasetViewerOwnProps {
  genomeId?: number; // genome ID or -1 for global case
  viewId?: number;
  defaultDataSeries?: string[]; // names of data series to be shown by default (i.e. ["GAL10"])
  groupId?: number;
  groupTableSelectionSpec?: InputTableQuerySpec;
  datasetId?: number;

  handleDatasetChanged: (datasetId: number) => void; // in order to change the URL as single point of truth
}

interface IDatasetViewerAppStateProps {
  viewer: IDatasetViewerState;
  isLoading: boolean;
  splitPaneLayout: SplitPaneLayoutState;
}

interface IDatasetViewerDispatchProps {
  loadDataset: (datasetId: number, defaultDataSeries) => Promise<any>;
  loadDefaultDatasetId: (genomeId: number, defaultDataSeries?: string[]) => Promise<number>;
  loadAvailableDatasets: (genomeId: number) => void;

  loadView: (viewId: number) => Promise<View>;
  chartLayoutStateChanged: (chartLayout: any) => void;
  colorSettingsChanged: (colorSettings: IColorSettings) => void;
  selectedConditionsChanged: (selectedAnnotations: ColumnAnnotation[]) => void;
  filteredConditionsChanged: (conditionField: string, conditionFilter: IColumnAnnotationFilter) => void;

  selectedDataSeriesChanged: (selectedDataSeries: SelectedDataSeries[]) => void;
  splitByCondition: (dataSeries: SelectedDataSeries, splitCondition: ColumnAnnotation) => void;
  addDataSeries: (dataSeriesName: string) => void;
  addGroupDataSeries: (dataSeriesName: string, groupTableSelectionSpec) => void;
  searchDataSeries: (searchTerm: string, datasetId) => Promise<DataSeries[]>;
  searchGroupDataSeries: (searchTerm: string, tableSpec) => Promise<DataSeries[]>;
  handleSplitPanelLayoutStateChange: (splitPaneLayout: SplitPaneLayoutState) => void;

  toggleFilterPanel: () => void;
  showViewLoadingWarning: () => void;
}

export interface IDatasetViewerProps
  extends IDatasetViewerOwnProps,
    IDatasetViewerAppStateProps,
    IDatasetViewerDispatchProps {}

function mapStateToProps(state: IAppState): IDatasetViewerAppStateProps {
  return {
    viewer: state.datasetViewer,
    isLoading: state.datasetViewer.isLoading || state.savedView.isLoading, // to account for initializing from data set or view
    splitPaneLayout: state.datasetViewer.splitPaneLayout,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch): IDatasetViewerDispatchProps {
  return {
    loadDataset: (datasetId: number, defaultDataSeries) =>
      dispatch(loadDatasetAndDataSeries(datasetId, defaultDataSeries)),
    loadDefaultDatasetId: (genomeId: number) => dispatch(getDefaultDatasetId(genomeId)),
    loadAvailableDatasets: (genomeId: number) => dispatch(loadAvailableDatasets(genomeId)),
    loadView: (viewId: number) => dispatch(loadView(viewId)),
    addDataSeries: (dataSeriesName: string) => dispatch(addDataSeries(dataSeriesName)),
    addGroupDataSeries: (dataSeriesName: string, groupTableSelectionSpec) =>
      dispatch(addGroupDataSeries(dataSeriesName, groupTableSelectionSpec)),
    chartLayoutStateChanged: (chartLayout: ChartLayoutState) => {
      dispatch(chartLayoutStateChanged(chartLayout));
    },
    colorSettingsChanged: (colorSettings: IColorSettings) => dispatch(colorSettingsChanged(colorSettings)),
    selectedConditionsChanged: (selectedAnnotations: ColumnAnnotation[]) => {
      dispatch(selectedConditionsChanged(selectedAnnotations));
    },
    filteredConditionsChanged: (conditionField: string, conditionFilter: IColumnAnnotationFilter) =>
      dispatch(filteredConditionsChanged(conditionField, conditionFilter)),
    selectedDataSeriesChanged: (selectedDataSeries: SelectedDataSeries[]) =>
      dispatch(selectedDataSeriesChanged(selectedDataSeries)),
    searchDataSeries: (searchTerm: string, datasetId) => dispatch(searchDataSeries(searchTerm, datasetId)),
    searchGroupDataSeries: (searchTerm: string, groupTableSelectionSpec) =>
      dispatch(searchGroupDataSeries(searchTerm, groupTableSelectionSpec)),
    splitByCondition: (dataSeries: SelectedDataSeries, splitCondition: ColumnAnnotation | null) =>
      dispatch(splitBySelectedCondition(dataSeries, splitCondition)),
    toggleFilterPanel: () => dispatch(toggleFilterPanel()),
    handleSplitPanelLayoutStateChange: (splitPaneLayout: SplitPaneLayoutState) =>
      dispatch(splitPaneLayoutStateChanged(splitPaneLayout)),
    showViewLoadingWarning: () =>
      notify.warning(
        {
          title: selMessage({ id: "dataViewer.viewLoading.warningTitle" }),
          children: selMessage({ id: "dataViewer.viewLoading.warningMessage" }),
        },
        { autoClose: false },
      ),
  };
}

/**
 * The dataset viewer component is the top level smart component analytical data viewer.
 *
 * created on 2/24/2017
 */
class DatasetViewer extends PureComponent<IDatasetViewerProps> {
  state = {
    showFilterPanel: false,
  };

  apiRef = createRef<PanelGroupRef>();

  componentDidMount() {
    this.init();

    // We are loading the datasets for all genomes in all situations, even from gene context!
    // This is because datasets may contain data from multiple genomes, but we currently can
    // only assign one via db schema. Later, we may supply the proper genomeId here.
    // TODO better load from the DatasetSelectionDialog !
    this.props.loadAvailableDatasets(UNSPECIFIED_ENTITY_ID);

    if (this.props.splitPaneLayout.hidden) {
      this.apiRef.current?.closePanel();
    }
  }

  componentDidUpdate(prevProps: Readonly<IDatasetViewerProps>) {
    // defaultDataSeries can change if we paginate through genes/entities
    const viewDiffers: boolean = this.props.viewId && this.props.viewId !== prevProps.viewId;
    const modeDiffers: boolean = !isNaN(this.props.groupId) && this.props.groupId !== prevProps.groupId;
    const genomeDiffers: boolean = Number.isInteger(this.props.genomeId) && this.props.genomeId !== prevProps.genomeId;
    const datasetDiffers: boolean = this.props.datasetId && this.props.datasetId !== prevProps.datasetId;
    const dataSeriesDiffer: boolean =
      (this.props.defaultDataSeries || []).join() !== (prevProps.defaultDataSeries || []).join();

    if (viewDiffers || genomeDiffers || dataSeriesDiffer || modeDiffers || datasetDiffers) {
      this.init();
    }

    if (this.props.splitPaneLayout.hidden) {
      this.apiRef.current?.closePanel();
    } else {
      this.apiRef.current?.openPanel();
    }
  }

  init() {
    if (Number.isInteger(this.props.genomeId) && !this.props.viewId && !this.props.groupId && !this.props.datasetId) {
      this.showDefaultDataset();
    } else if (this.props.viewId) {
      this.props.loadView(this.props.viewId).then((view) => {
        if (view.viewType === VIEW.DATA_VIEW.type && !view.state.datasetView.dataset) {
          this.props.showViewLoadingWarning();
        }
      });
    } else if (this.props.datasetId) {
      this.props.loadDataset(this.props.datasetId, this.props.defaultDataSeries);
    }
  }

  showDefaultDataset = () => {
    this.props.loadDefaultDatasetId(this.props.genomeId).then((defaultDatasetId: number) => {
      if (defaultDatasetId) {
        this.props.handleDatasetChanged(defaultDatasetId);
      }
    });
  };

  handleSelectedConditionsChanged = (selectedConditions: ColumnAnnotation[]) => {
    this.props.selectedConditionsChanged(selectedConditions);
    if (selectedConditions.length > 1) {
      this.props.chartLayoutStateChanged({ timeSeries: false });
    }
  };

  handleFilterPanelToggle = () => {
    this.props.toggleFilterPanel();
  };

  render(): ReactElement {
    const isLoading: boolean = this.props.isLoading || (this.props.groupId && !this.props.groupTableSelectionSpec);
    const viewer: IDatasetViewerState = this.props.viewer;
    const splitPaneLayout: SplitPaneLayoutState = this.props.splitPaneLayout;

    // Show a loading spinner if the data is not yet available
    if (isLoading) {
      return <LoadingSpinner inCenter />;
    }

    const chart = (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "stretch", width: "100%", height: "100%" }}>
        {this.props.viewer.showFilterPanel && (
          <div className={"chart-filters-sidebar"}>
            <FilterPanel
              dataset={viewer.dataset}
              filteredOutAnnotations={viewer.filteredOutAnnotations}
              filteredOutColumns={viewer.filteredOutColumns}
              filteredOutValueIndicesByColumn={viewer.filteredOutValueIndicesByColumn}
              availableAnnotations={viewer.availableAnnotations}
              selectedAnnotations={viewer.selectedAnnotations}
              onFilteredConditionsChanged={this.props.filteredConditionsChanged}
              closeFilterPanel={this.handleFilterPanelToggle}
            />
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            // The filters have a fixed width of 320px
            width: this.props.viewer.showFilterPanel ? "calc(100% - 320px)" : "100%",
            height: "100%",
          }}
        >
          <ChartToolbar
            key={"chart-toolbar"}
            chartLayout={viewer.chartLayout}
            selectedDataSeries={viewer.selectedDataSeries}
            selectedAnnotations={viewer.selectedAnnotations}
            isTimeSeriesToggleEnabled={isTimeSeriesEnabled(viewer.selectedAnnotations)}
            onChartLayoutStateChanged={this.props.chartLayoutStateChanged}
          />
          <DatasetChart key={"dataset-chart"} />
        </div>
      </div>
    );

    // Show the settings on the left and the chart itself on the right
    return (
      <TipTour
        tips={getDataViewerPageTips(this.props.viewer.mode === DATA_VIEWER_MODE.GROUP, !!viewer.dataset).filter(
          (tip: ITip) => !tip.disableBeacon,
        )}
      >
        <ResizableTabPanels
          orientation={PanelOrientation.Horizontal}
          defaultSize={320}
          minSize={200}
          maxSize={500}
          fixedPanel={PanelPosition.Leading}
        >
          <ResizableTabPanels.FixedPanel>
            <div className={styleLayout.grid_pane} style={{ padding: 10, overflowY: "auto" }}>
              <ChartSettings
                viewerState={viewer}
                defaultDataSeries={this.props.defaultDataSeries}
                globalContext={this.props.genomeId === UNSPECIFIED_ENTITY_ID}
                groupId={this.props.groupId}
                onSelectedConditionsChanged={this.handleSelectedConditionsChanged}
                onSelectedDatasetChanged={this.props.handleDatasetChanged}
                onSelectedDataSeriesChanged={this.props.selectedDataSeriesChanged}
                onSearchDataSeries={this.searchDataSeries}
                onAddDataSeries={this.addDataSeries}
                onColorSettingsChanged={this.props.colorSettingsChanged}
                onChartLayoutStateChanged={this.props.chartLayoutStateChanged}
                onSplitByCondition={this.props.splitByCondition}
                onFilterPanelToggle={this.handleFilterPanelToggle}
              />
            </div>
          </ResizableTabPanels.FixedPanel>

          <ResizableTabPanels.FlexPanel>
            {/* If working in sample mode, we wrap the chart into a split pane (together with the data table), otherwise we show it right away*/}
            {this.props.viewer.mode === DATA_VIEWER_MODE.GROUP ? (
              <ResizableTabPanels
                style={{ padding: 0 }}
                orientation={splitPaneLayout.verticalLayout ? PanelOrientation.Vertical : PanelOrientation.Horizontal}
                apiRef={this.apiRef}
                defaultSize={splitPaneLayout.size}
                onMoveEnd={(size, _, isClosed) => {
                  this.props.handleSplitPanelLayoutStateChange({
                    ...splitPaneLayout,
                    hidden: isClosed,
                    size,
                  });
                }}
                fixedPanel={PanelPosition.Trailing}
              >
                <ResizableTabPanels.FlexPanel>
                  <div className={styleLayout.grid_pane}>{chart}</div>
                </ResizableTabPanels.FlexPanel>
                <ResizableTabPanels.FixedPanel>
                  <div className={styleLayout.grid_pane}>
                    <DatasetViewerTable groupId={this.props.groupId} />
                  </div>
                </ResizableTabPanels.FixedPanel>
              </ResizableTabPanels>
            ) : (
              <div className={styleLayout.grid_pane}>{chart}</div>
            )}
          </ResizableTabPanels.FlexPanel>
        </ResizableTabPanels>
      </TipTour>
    );
  }

  searchDataSeries = (searchTerm: string) => {
    return this.props.groupId
      ? this.props.searchGroupDataSeries(searchTerm, this.props.groupTableSelectionSpec)
      : this.props.searchDataSeries(searchTerm, this.props.datasetId);
  };

  addDataSeries = (dataSeriesName: string) => {
    return this.props.groupId
      ? this.props.addGroupDataSeries(dataSeriesName, this.props.groupTableSelectionSpec)
      : this.props.addDataSeries(dataSeriesName);
  };
}

export default connect<IDatasetViewerAppStateProps, IDatasetViewerDispatchProps, IDatasetViewerOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(DatasetViewer);
